'use client';

import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Button, DeprecatedLink as Link, Text } from '@lmn/design-system';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useFetchGuestReviews } from '@/app/components/hotel-detail/hooks/useFetchGuestReviews';
import {
  type GuestReviewRatingScale,
  ReviewCardWithReadMore,
  ReviewContainer,
} from '@/app/components/hotel-detail/guest-reviews/GuestReviewCard';
import { ReviewsListModal } from '@/app/components/hotel-detail/guest-reviews/GuestReviewsModal';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import type { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useIsMobile } from '../hooks/useMediaSSR';

type GuestReviewsProps = {
  /** Rendered in the Evo details modal: the section owns the title and the tab the inset. */
  inline?: boolean;
  ratingScale?: GuestReviewRatingScale;
} & React.HTMLAttributes<HTMLElement>;

export default function GuestReviews(props: GuestReviewsProps) {
  const { inline = false, ratingScale, ...rest } = props;
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { staticData, commonData } = usePageWrapperData();
  const { isEvoLayout } = useCommonClient();
  const isMobile = useIsMobile(commonData.isMobileDevice);

  const [isReviewsListModalOpen, setReviewsListModalOpen] = useState<boolean | undefined>();

  const { data: hotelReviews } = useFetchGuestReviews();

  useEffect(() => {
    if (hotelReviews?.[0]?.data?.reviews?.length) {
      sendCuriosityClickEvent(
        {
          event_name: 'event_guest_reviews_shown',
        },
        { onlyOnce: true }
      );
    }
  }, [hotelReviews]);

  const onViewMoreReviewsClick = () => {
    setReviewsListModalOpen(true);
    sendCuriosityClickEvent({
      event_name: 'click_see_all_guest_reviews',
    });
  };

  const showMoreReviews = (hotelReviews?.[0].data?.reviews?.length ?? 0) > (isMobile ? 5 : 3);

  return hotelReviews?.[0]?.data?.reviews?.length ? (
    <GuestReviewsContainer $inline={inline} {...rest}>
      {!isMobile && !inline && (
        <Text
          variant="Title5"
          css={`
            margin-bottom: 8px;
          `}
        >
          {t('vacation.select.details.guest.reviews.latest')}
        </Text>
      )}
      <ReviewsList css={showMoreReviews ? 'margin-bottom: 16px;' : undefined} tabIndex={-1}>
        {hotelReviews?.[0].data?.reviews?.slice(0, isMobile ? 5 : 3).map((review, index) => (
          <ReviewCardWithReadMore
            // eslint-disable-next-line react/no-array-index-key
            key={`${review.name}-${index}`}
            {...review}
            logoUrl={hotelReviews?.[0]?.data?.sources[review.source]?.logo}
            ratingScale={ratingScale}
          />
        ))}
      </ReviewsList>
      {showMoreReviews ? (
        <ShowMoreReviews>
          {isMobile ? (
            <Button
              variant="primary"
              appearance="outlined"
              onClick={onViewMoreReviewsClick}
              css={`
                width: 100%;
              `}
            >
              {t('vacation.select.details.guest.reviews.list.show')}
            </Button>
          ) : (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              variant="accent"
              role="link"
              onClick={onViewMoreReviewsClick}
              css={css`
                ${({ theme }) => theme.kallisto.mixins.font('BodySmall')}
              `}
            >
              {t('vacation.select.details.guest.reviews.list.show')}
            </Link>
          )}
        </ShowMoreReviews>
      ) : null}

      <OpenXModal
        isOpen={isReviewsListModalOpen ?? false}
        isMobile={isMobile ?? true}
        brand={staticData.domainValues?.brand as Brand}
        onClose={() => setReviewsListModalOpen(false)}
        onCloseByBrowserHistory={() => setReviewsListModalOpen(false)}
        onOpenByBrowserHistory={() => setReviewsListModalOpen(true)}
        fullScreen={!isMobile}
        // Inline, the list opens over the Evo details modal, so it takes the same lateral width.
        className={
          isMobile
            ? 'hotel-reviews-modal'
            : `hotel-reviews-modal ${inline ? 'hotel-modal-lateral-evo' : 'hotel-modal-lateral'}`
        }
        id="guest-reviews-section-modal"
        aria-label={t('vacation.select.details.guest.reviews.title')}
        bodyBackgroundColor="backgroundPage"
        // ModalHeader switches to the Evo header (close button on the right) on Evo pages, whose
        // scroll-driven shadow needs scrollShadows.
        scrollShadows={isEvoLayout}
        header={{
          sticky: true,
          element: (
            <ModalHeader handleClose={() => setReviewsListModalOpen(false)}>
              {t('vacation.select.details.guest.reviews.title')}
            </ModalHeader>
          ),
        }}
      >
        <ReviewsListModal ratingScale={ratingScale} />
      </OpenXModal>
    </GuestReviewsContainer>
  ) : null;
}

export const GuestReviewsContainer = styled.div<{ $inline?: boolean }>`
  background-color: ${(p) => p.theme.commons.colors.gray0};

  ${({ $inline }) =>
    !$inline &&
    css`
      @media ${customCss.breakpoints.sm} {
        border-radius: 8px;
        padding: 16px;
      }

      @media ${customCss.breakpoints.xl} {
        padding: 24px;
      }
    `}
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;

  padding: 0 16px;
  margin: 0 -16px;

  @media ${customCss.breakpoints.sm} {
    padding: 0;
    margin: 0;
  }

  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  scroll-snap-type: x mandatory;

  ${ReviewContainer} {
    scroll-snap-align: center;
  }
`;

export const ShowMoreReviews = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;
